import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { useMobileChat } from '../../context/MobileChatContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  ChevronLeft, 
  Calendar,
  Loader2,
  MapPin,
  Truck,
  User,
  Phone,
  Mail,
  Building2,
  FileText,
  Clock,
  Wrench,
  Home,
  CheckCircle2,
  ClipboardList,
  Navigation,
  Map,
  MessageSquare,
  ChevronDown,
  Package,
  Mountain
} from 'lucide-react';
import { format } from 'date-fns';

// Fix for default marker icon in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom yellow marker for job location
const jobMarkerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const MobileJobDetail = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { api } = useMobileAuth();
  const { openJobChat } = useMobileChat();
  const dateFilter = searchParams.get('date') || 'today';
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coordinates, setCoordinates] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [jobForms, setJobForms] = useState([]);
  const [showFormsSection, setShowFormsSection] = useState(false);
  const [selectedViewingDate, setSelectedViewingDate] = useState(null);

  // Calculate the viewing date based on dateFilter or selected date
  const getViewingDate = () => {
    // If user has selected a specific date, use that
    if (selectedViewingDate) {
      return selectedViewingDate;
    }
    
    const today = new Date();
    if (dateFilter === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday.toISOString().split('T')[0];
    } else if (dateFilter === 'tomorrow') {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    } else if (dateFilter === 'today') {
      return today.toISOString().split('T')[0];
    }
    // If dateFilter is an actual date string (YYYY-MM-DD), use it directly
    return dateFilter;
  };

  useEffect(() => {
    fetchJobDetail();
    fetchJobForms();
  }, [jobId, dateFilter, selectedViewingDate]);

  // Geocode the address when job data is loaded
  useEffect(() => {
    if (job) {
      geocodeAddress();
    }
  }, [job]);

  const fetchJobDetail = async () => {
    setLoading(true);
    try {
      const viewingDate = getViewingDate();
      // Get user's LOCAL date (not UTC) for accurate timezone handling
      const now = new Date();
      const userToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const response = await api.get(`/api/mobile/jobs/${jobId}?viewing_date=${viewingDate}&user_today=${userToday}`);
      setJob(response.data);
    } catch (error) {
      toast.error('Failed to fetch job details');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobForms = async () => {
    try {
      const response = await api.get('/api/form-templates');
      // Filter for job-linked, active forms
      const jobLinkedForms = response.data.filter(
        t => t.is_active && t.form_type === 'job_linked'
      );
      setJobForms(jobLinkedForms);
    } catch (error) {
      console.error('Failed to fetch job forms:', error);
    }
  };

  const handleStartJobForm = (template) => {
    navigate(`/mobile/forms/submit?templateId=${template.id}&jobId=${jobId}`);
  };

  // Build full address string from job data
  const getFullAddress = () => {
    if (!job) return '';
    
    const parts = [];
    
    // Use site address if available, otherwise use one-off address
    const street = job.site?.street_address || job.one_off_street;
    const suburb = job.site?.suburb || job.one_off_suburb;
    const state = job.site?.state || job.one_off_state;
    const postcode = job.site?.postcode || job.one_off_postcode;
    
    if (street) parts.push(street);
    if (suburb) parts.push(suburb);
    if (state) parts.push(state);
    if (postcode) parts.push(postcode);
    
    // Add Australia for better geocoding results
    if (parts.length > 0) {
      parts.push('Australia');
    }
    
    return parts.join(', ');
  };

  // Geocode the address using OpenStreetMap Nominatim (free service)
  const geocodeAddress = async () => {
    const address = getFullAddress();
    if (!address || address === 'Australia') {
      return;
    }
    
    setGeocoding(true);
    try {
      const encodedAddress = encodeURIComponent(address);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`,
        {
          headers: {
            'User-Agent': 'EQUIPR-Mobile-App'
          }
        }
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        setCoordinates({
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          displayName: data[0].display_name
        });
      }
    } catch (error) {
      console.error('Geocoding failed:', error);
    } finally {
      setGeocoding(false);
    }
  };

  // Open native maps app for directions
  const openDirections = () => {
    const address = getFullAddress();
    if (!address) {
      toast.error('No address available for this job');
      return;
    }
    
    // Encode the address for URL
    const encodedAddress = encodeURIComponent(address);
    
    // Detect platform and open appropriate maps app
    // This will work on both iOS and Android
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      // Apple Maps
      window.open(`maps://maps.apple.com/?daddr=${encodedAddress}`, '_blank');
    } else {
      // Google Maps (works on Android and as fallback)
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.slice(0, 5);
  };

  // Use backend's can_create_docket instead of local calculation
  // This properly handles multi-day jobs and date-specific docket checking

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <p className="text-gray-400">Job not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a] pb-48">
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-[1000]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/mobile/jobs')} className="text-gray-400 p-2">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-white font-bold">Job Details</h1>
              <p className="text-gray-400 text-xs">{formatDate(job.start_date)}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/mobile/dashboard')}
            className="text-gray-400 p-2"
            data-testid="home-btn"
          >
            <Home className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Time & Status */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-[#ffd108]" />
                </div>
                <div>
                  <p className="text-[#ffd108] font-bold text-xl">
                    {formatTime(job.start_time)}{job.end_time ? ` - ${formatTime(job.end_time)}` : ''}
                  </p>
                  {/* Show multi-day info if applicable */}
                  {job.is_multi_day ? (
                    <p className="text-gray-400 text-sm">
                      {formatDate(job.start_date)} - {formatDate(job.end_date)}
                    </p>
                  ) : (
                    <p className="text-gray-400 text-sm">{formatDate(job.start_date)}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge className={job.status === 'docketed' ? 'bg-green-900 text-green-300' : 'bg-blue-900 text-blue-300'}>
                  {job.status}
                </Badge>
                {job.is_multi_day && (
                  <Badge className="bg-purple-900 text-purple-300 text-xs">
                    Multi-day
                  </Badge>
                )}
                {job.operator_role === 'additional_worker' && job.worker_type_name && (
                  <Badge className="bg-cyan-900 text-cyan-300 text-xs">
                    {job.worker_type_name}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Date Selector for Multi-day Jobs */}
        {job.is_multi_day && job.job_dates_info && (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#ffd108]" />
                  <span className="text-white text-sm font-medium">Viewing Date</span>
                </div>
                <Select
                  value={selectedViewingDate || job.viewing_date}
                  onValueChange={(value) => setSelectedViewingDate(value)}
                >
                  <SelectTrigger className="w-[180px] bg-[#1a1a1a] border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2a2a2a] border-gray-600">
                    {job.job_dates_info.map((dateInfo) => (
                      <SelectItem 
                        key={dateInfo.date} 
                        value={dateInfo.date}
                        className="text-white hover:bg-[#3a3a3a]"
                      >
                        <div className="flex items-center gap-2">
                          <span>{format(new Date(dateInfo.date + 'T00:00:00'), 'EEE, d MMM')}</span>
                          {dateInfo.has_docket && (
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                          )}
                          {dateInfo.is_today && (
                            <Badge className="bg-blue-900 text-blue-300 text-[10px] px-1">Today</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Show docket status summary */}
              <div className="mt-3 flex items-center gap-4 text-xs">
                <span className="text-gray-400">
                  {job.job_dates_info.filter(d => d.has_docket).length} of {job.job_dates_info.length} days docketed
                </span>
                {job.has_docket_for_date && (
                  <Badge className="bg-green-900 text-green-300 text-[10px]">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Docketed
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer Info */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[#ffd108]" />
              Customer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-white font-medium text-lg">{job.customer?.company_name}</p>
            {!job.customer_contact && job.customer?.contact_name && (
              <p className="text-gray-400 text-sm flex items-center gap-2">
                <User className="h-4 w-4" />
                {job.customer.contact_name}
              </p>
            )}
            {!job.customer_contact && job.customer?.phone && (
              <a href={`tel:${job.customer.phone}`} className="text-[#ffd108] text-sm flex items-center gap-2">
                <Phone className="h-4 w-4" />
                {job.customer.phone}
              </a>
            )}
          </CardContent>
        </Card>

        {/* Contacts Section - Job Contact and Site Contact */}
        {(job.customer_contact || job.site_contact) && (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <User className="h-4 w-4 text-[#ffd108]" />
                Contacts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Job Contact (Customer Contact for this job) */}
              {job.customer_contact && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Job Contact</p>
                  <p className="text-white font-medium flex items-center gap-2">
                    {job.customer_contact.name}
                    {job.customer_contact.position && (
                      <span className="text-gray-400 text-sm">({job.customer_contact.position})</span>
                    )}
                  </p>
                  {job.customer_contact.phone && (
                    <a href={`tel:${job.customer_contact.phone}`} className="text-[#ffd108] text-sm flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {job.customer_contact.phone}
                    </a>
                  )}
                  {job.customer_contact.email && (
                    <a href={`mailto:${job.customer_contact.email}`} className="text-gray-400 text-sm flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {job.customer_contact.email}
                    </a>
                  )}
                </div>
              )}

              {/* Divider if both contacts exist */}
              {job.customer_contact && job.site_contact && (
                <div className="border-t border-gray-700" />
              )}

              {/* Site Contact */}
              {job.site_contact && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Site Contact</p>
                  <p className="text-white font-medium flex items-center gap-2">
                    {job.site_contact.name}
                    {job.site_contact.job_title && (
                      <span className="text-gray-400 text-sm">({job.site_contact.job_title})</span>
                    )}
                  </p>
                  {job.site_contact.phone && (
                    <a href={`tel:${job.site_contact.phone}`} className="text-[#ffd108] text-sm flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {job.site_contact.phone}
                    </a>
                  )}
                  {job.site_contact.email && (
                    <a href={`mailto:${job.site_contact.email}`} className="text-gray-400 text-sm flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {job.site_contact.email}
                    </a>
                  )}
                  {job.site_contact.notes && (
                    <p className="text-gray-500 text-sm">{job.site_contact.notes}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Location with Map */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#ffd108]" />
              Location
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-white font-medium">{job.site?.name || 'One-off Location'}</p>
              {job.site?.address && (
                <p className="text-gray-400 text-sm mt-1">{job.site.address}</p>
              )}
              {(job.site?.street_address || job.one_off_street) && (
                <p className="text-gray-400 text-sm mt-1">
                  {job.site?.street_address || job.one_off_street}
                  {(job.site?.suburb || job.one_off_suburb) && `, ${job.site?.suburb || job.one_off_suburb}`}
                  {(job.site?.state || job.one_off_state) && ` ${job.site?.state || job.one_off_state}`}
                  {(job.site?.postcode || job.one_off_postcode) && ` ${job.site?.postcode || job.one_off_postcode}`}
                </p>
              )}
            </div>

            {/* Map Preview */}
            <div className="rounded-lg overflow-hidden border border-gray-600">
              {geocoding ? (
                <div className="h-40 bg-[#1a1a1a] flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" />
                  <span className="ml-2 text-gray-400 text-sm">Loading map...</span>
                </div>
              ) : coordinates ? (
                <MapContainer
                  center={[coordinates.lat, coordinates.lng]}
                  zoom={15}
                  style={{ height: '160px', width: '100%' }}
                  zoomControl={false}
                  attributionControl={false}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[coordinates.lat, coordinates.lng]} icon={jobMarkerIcon}>
                    <Popup>
                      <div className="text-sm">
                        <strong>{job.site?.name || 'Job Location'}</strong>
                        <br />
                        {job.customer?.company_name}
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>
              ) : (
                <div className="h-40 bg-[#1a1a1a] flex items-center justify-center">
                  <div className="text-center">
                    <Map className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Map unavailable</p>
                    <p className="text-gray-600 text-xs">No address coordinates found</p>
                  </div>
                </div>
              )}
            </div>

            {/* Get Directions Button */}
            <Button 
              onClick={openDirections}
              className="w-full bg-[#ffd108] hover:bg-[#e6bc00] text-[#1a1a1a] font-medium"
            >
              <Navigation className="h-4 w-4 mr-2" />
              Get Directions
            </Button>
          </CardContent>
        </Card>

        {/* Equipment */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Truck className="h-4 w-4 text-[#ffd108]" />
              Equipment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-white font-medium">{job.equipment?.name}</p>
            <p className="text-gray-400 text-sm">Asset ID: {job.equipment?.asset_id}</p>
            <Badge variant="outline" className="mt-2 border-gray-600 text-gray-300">
              {job.equipment_type}
            </Badge>
          </CardContent>
        </Card>

        {/* Attachments */}
        {job.attachment_types && job.attachment_types.length > 0 && (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Wrench className="h-4 w-4 text-[#ffd108]" />
                Attachments Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {job.attachment_types.map((att) => (
                  <Badge key={att.id} variant="secondary" className="bg-gray-700 text-gray-300">
                    {att.name}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Workers Assigned */}
        {job.worker_allocations && job.worker_allocations.length > 0 && (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <User className="h-4 w-4 text-[#ffd108]" />
                Workers Assigned
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {job.worker_allocations.map((wa, idx) => {
                  const workerType = job.worker_types?.find(wt => wt.id === wa.worker_type_id);
                  return (
                    <div key={wa.id || idx} className="flex items-center justify-between bg-[#1a1a1a] p-3 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                          <User className="h-5 w-5 text-[#ffd108]" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{wa.worker_name || 'Not Assigned'}</p>
                          {wa.worker_phone ? (
                            <a href={`tel:${wa.worker_phone}`} className="text-[#ffd108] text-sm hover:underline">
                              {wa.worker_phone}
                            </a>
                          ) : (
                            <p className="text-gray-500 text-sm">No phone</p>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline" className="border-gray-600 text-gray-300">
                        {workerType?.name || 'Worker'}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Description */}
        {job.description && (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-[#ffd108]" />
                Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-300 text-sm">{job.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {job.notes && (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 text-sm">{job.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Submitted Docket Data - Only show docket for current viewing date */}
        {job.viewing_date_docket && (
          <Card className="bg-[#2a2a2a] border-gray-700 border-l-4 border-l-green-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Submitted Docket - {formatDate(job.viewing_date)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {/* Docket Header */}
                <div className="flex items-center justify-between">
                  <Badge className="bg-green-900 text-green-300">
                    {job.viewing_date_docket.docket_number || 'Draft'}
                  </Badge>
                  <span className="text-xs text-gray-400">
                    {job.viewing_date_docket.job_date}
                  </span>
                </div>

                {/* Docket Items - Filter out material items */}
                {job.viewing_date_docket.items && job.viewing_date_docket.items.filter(item => item.item_type !== 'material').length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Hours Recorded</p>
                    {job.viewing_date_docket.items.filter(item => item.item_type !== 'material').map((item, idx) => (
                      <div key={item.id || idx} className="bg-[#1a1a1a] p-2 rounded">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {item.item_type === 'equipment' ? (
                              <Truck className="h-3 w-3 text-[#ffd108]" />
                            ) : item.item_type === 'worker' ? (
                              <User className="h-3 w-3 text-blue-400" />
                            ) : (
                              <Wrench className="h-3 w-3 text-gray-400" />
                            )}
                            <span className="text-gray-300 text-sm">{item.display_name || item.type_name}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[#ffd108] font-medium text-sm">{item.qty}h</span>
                            {item.start_time && item.finish_time && (
                              <span className="text-gray-500 text-xs ml-2">
                                ({item.start_time} - {item.finish_time})
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Lunch & Travel breakdown */}
                        {(item.lunch_minutes > 0 || item.travel_minutes > 0) && (
                          <div className="flex items-center gap-3 mt-1 ml-5 text-xs text-gray-500">
                            {item.lunch_minutes > 0 && (
                              <span>Lunch: -{item.lunch_minutes}min</span>
                            )}
                            {item.travel_minutes > 0 && (
                              <span>Travel: +{item.travel_minutes}min</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Material Loads */}
                {job.viewing_date_docket.material_loads && job.viewing_date_docket.material_loads.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                      <Mountain className="h-3 w-3 text-orange-500" />
                      Material Loads
                    </p>
                    {job.viewing_date_docket.material_loads.map((load, idx) => (
                      <div key={load.id || idx} className="bg-[#1a1a1a] p-2 rounded">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Package className="h-3 w-3 text-orange-500" />
                            <div>
                              <span className="text-gray-300 text-sm">{load.material_name}</span>
                              <p className="text-gray-500 text-xs">{load.yard_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-orange-500 font-medium text-sm">{load.quantity} {load.unit}</span>
                            <p className={`text-xs ${
                              load.load_type === 'pickup' ? 'text-blue-400' :
                              load.load_type === 'disposal' ? 'text-amber-400' :
                              'text-green-400'
                            }`}>
                              {load.load_type === 'pickup' ? 'Pickup' : 
                               load.load_type === 'disposal' ? 'Disposal' : 'Buy Back'}
                            </p>
                          </div>
                        </div>
                        {load.tip_docket_number && (
                          <p className="text-gray-500 text-xs mt-1 ml-5">Docket #: {load.tip_docket_number}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Work Completed */}
                {job.viewing_date_docket.work_completed && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Work Completed</p>
                    <p className="text-gray-300 text-sm bg-[#1a1a1a] p-2 rounded">{job.viewing_date_docket.work_completed}</p>
                  </div>
                )}

                {/* Notes */}
                {job.viewing_date_docket.notes && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Docket Notes</p>
                    <p className="text-gray-400 text-sm bg-[#1a1a1a] p-2 rounded">{job.viewing_date_docket.notes}</p>
                  </div>
                )}

                {/* Customer Sign-off */}
                {job.viewing_date_docket.customer_signoff_confirmed && (
                  <div className="border-t border-gray-700 pt-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Customer Sign-off</p>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <div>
                        <p className="text-gray-300 text-sm font-medium">{job.viewing_date_docket.customer_signoff_name || 'Customer'}</p>
                        <p className="text-gray-500 text-xs">Confirmed & Signed</p>
                      </div>
                    </div>
                    {job.viewing_date_docket.customer_signoff_signature && (
                      <div className="mt-2 bg-white rounded p-2 inline-block">
                        <img 
                          src={job.viewing_date_docket.customer_signoff_signature} 
                          alt="Customer Signature" 
                          className="h-12 max-w-[150px] object-contain"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Submitted Time */}
                <div className="text-xs text-gray-500 text-right">
                  Submitted: {new Date(job.viewing_date_docket.created_at).toLocaleString()}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#2a2a2a] border-t border-gray-700 p-4 z-[1000] max-h-[50vh] overflow-y-auto">
        {/* For multi-day jobs with outstanding dockets, show create button with date selector hint */}
        {job.is_multi_day && job.has_outstanding_dockets && !job.can_create_docket && job.has_docket_for_date ? (
          <div className="space-y-2">
            <Button className="w-full bg-green-900 text-green-300 cursor-default" disabled>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Docketed for {formatDate(job.viewing_date)}
            </Button>
            <p className="text-xs text-center text-gray-400">
              Select a different date above to create docket for another day
            </p>
          </div>
        ) : job.has_docket_for_date ? (
          <Button className="w-full bg-green-900 text-green-300 cursor-default" disabled>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Docket Submitted for {formatDate(job.viewing_date)}
          </Button>
        ) : job.can_create_docket ? (
          <Button 
            className="w-full btn-volt" 
            onClick={() => navigate(`/mobile/docket/create/${job.id}?date=${job.viewing_date}`)}
          >
            <FileText className="h-4 w-4 mr-2" />
            Create Docket for {formatDate(job.viewing_date)}
          </Button>
        ) : job.is_multi_day && job.has_outstanding_dockets ? (
          <div className="space-y-2">
            <Button className="w-full" disabled variant="secondary">
              <Calendar className="h-4 w-4 mr-2" />
              {job.docket_unavailable_reason || 'Cannot create docket for this date'}
            </Button>
            <p className="text-xs text-center text-gray-400">
              Select today or yesterday from the date picker above
            </p>
          </div>
        ) : (
          <Button className="w-full" disabled variant="secondary">
            <Calendar className="h-4 w-4 mr-2" />
            {job.docket_unavailable_reason || 'Docket not available'}
          </Button>
        )}
        
        {/* Job Forms Section */}
        {jobForms.length > 0 && (
          <div className="mt-2">
            <Button 
              variant="outline"
              className="w-full bg-[#2a2a2a] border-gray-700 text-white hover:bg-purple-600 hover:border-purple-600"
              onClick={() => setShowFormsSection(!showFormsSection)}
            >
              <ClipboardList className="h-4 w-4 mr-2" />
              Job Forms ({jobForms.length})
              <span className={`ml-auto transition-transform ${showFormsSection ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </Button>
            
            {showFormsSection && (
              <div className="mt-2 space-y-2 bg-[#1a1a1a] rounded-lg p-2">
                {jobForms.map((form) => (
                  <button
                    key={form.id}
                    onClick={() => handleStartJobForm(form)}
                    className="w-full p-3 bg-[#2a2a2a] rounded-lg text-left hover:bg-[#3a3a3a] transition-colors flex items-center justify-between"
                  >
                    <div>
                      <p className="text-white text-sm font-medium">{form.name}</p>
                      {form.description && (
                        <p className="text-xs text-gray-400 mt-0.5">{form.description}</p>
                      )}
                    </div>
                    <FileText className="h-4 w-4 text-purple-400 flex-shrink-0 ml-2" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Job Chat Button */}
        <Button 
          variant="outline"
          className="w-full mt-2 bg-[#2a2a2a] border-gray-700 text-white hover:bg-blue-600 hover:border-blue-600"
          onClick={() => openJobChat(job.id)}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Chat with Office
        </Button>
      </div>
    </div>
  );
};

export default MobileJobDetail;
